import React from "react"
import LegacyAndV2 from '@components/CustomLayouts/LegacyAndV2'
import EndVideoModal from '@components/EndVideoModal'
import CallToAction from '@components/CallToAction'
import Headers from '@components/Headers'
import Footer from '@components/Layout/styles/Footer'
import LogoFooter from '@components/Layout/styles/Logo'
import LogoLink from '@components/Layout/styles/LogoLink'
import VideoMessageSign from '@components/Layout/styles/VideoMessageSign'
import Logo from '@components/Logo'
import RenderIf from '@components/RenderIf'
import SpinnerIcon from '@components/VideoPlayer/components/Scene/styles/SpinnerIcon'
import { environmentsConfig } from '@config'
import { MUX_STATUS } from '@constants/constants'
import { Backdrop } from '@material-ui/core'
import { Campaign as SharedCampaign } from '@sendspark/player/dist/types'
import ActionSection from './ActionSection'
import Description from './Description'
import DescriptionSection from './DescriptionSection'
import MainContent from './MainContent'
import Title from './Title'
import TitleSection from './TitleSection'
import VideoPreview from '../shared/VideoPreview'
import VideoSection from './VideoSection'
import dynamic from "next/dynamic"
import { BaseThemeProps } from "../interfaces"

const Player = dynamic(() => import('@components/Player'), { ssr: false })

export interface LegacyThemeProps extends BaseThemeProps {
  isViralModalOpen: boolean
  handleOnReplay: () => void
  setShowViralLoop: (show: boolean) => void
}

const LegacyTheme: React.FC<LegacyThemeProps> = ({
  campaign,
  env,
  pageTitle,
  isLinkedInRequest,
  isViralModalOpen,
  isEditPage,
  title,
  description,
  showCta,
  callToAction,
  isMultipleCTAsSupported,
  trackCTAClick,
  trackViralLink,
  viralLink,
  handleOnReplay,
  setShowViralLoop,
  replayVideo,
  setReplayVideo,
  isOwnerVideo,
  legacyAnalytics,
  playerEvents,
  playerEventsRef
}) => {
  const { style } = campaign
  return (
    <LegacyAndV2 props={{ campaign, env }} isV2={false}>
      <Headers
        title={pageTitle}
        campaign={campaign}
        sendsparkBrandingEnabled={campaign.sendsparkBrandingEnabled}
        isLinkedInRequest={isLinkedInRequest}
      />
      <Backdrop
        style={{
          zIndex: 10,
          backgroundColor: 'rgba(0,0,0,0.4)',
        }}
        open={isViralModalOpen}
      />
      <MainContent isEditPage={isEditPage}>
        <EndVideoModal
          isOpen={isViralModalOpen}
          campaign={campaign}
          onClose={() => setShowViralLoop(false)}
          handleOnReplay={handleOnReplay}
        />
        <TitleSection>
          <Logo src={campaign.meta.logo?.url} size={style.logoSize} />
          <RenderIf condition={!!title}>
            <Title color={style.heading1}>{title}</Title>
          </RenderIf>
        </TitleSection>
        <VideoSection>
          <RenderIf
            condition={
              campaign?.meta?.video?.muxStatus === MUX_STATUS.READY ||
              (campaign?.meta?.video?.uploadingStatus === 'pending' &&
                campaign?.meta?.video?.muxStatus === MUX_STATUS.PREPARING)
            }
          >
            <Player
              isEditPage={isEditPage}
              campaign={(campaign as unknown) as SharedCampaign}
              apiV2Url={env.apiV2Url}
              beaconUrl={env.beaconUrl}
                legacyAnalytics={legacyAnalytics}
              beaconDebug={env.beaconDebug}
              insightUrl={
                env.insightCaptureEnabled && !isOwnerVideo && env.insightUrl
                  ? env.insightUrl
                  : undefined
              }
              insightApiKey={
                env.insightCaptureEnabled && !isOwnerVideo && env.insightUrl
                  ? env.insightApiKey || undefined
                  : undefined
              }
              events={isOwnerVideo ? (null as any) : playerEvents}
              apiV1Url={environmentsConfig.apiV1Url}
              nodeEnv={environmentsConfig.nodeEnv}
              autoPlay={campaign?.featureFlips?.autoPlay && campaign?.autoPlay}
              thumbsUp={campaign?.featureFlips?.thumbsUp && campaign?.thumbsUp}
              gapCombinedVideoPlayer={
                campaign?.featureFlips?.gapCombinedVideoPlayer &&
                campaign?.gapCombinedVideoPlayer
              }
              replayVideo={replayVideo}
              setReplayVideo={setReplayVideo}
              playerEventsRef={playerEventsRef}
            />
          </RenderIf>
          <RenderIf
            condition={
              ['uploading', 'completed'].includes(
                campaign?.meta?.video?.uploadingStatus || ''
              ) && campaign?.meta?.video?.muxStatus === MUX_STATUS.PREPARING
            }
          >
            <VideoPreview>
              <span>
                <div>
                  <SpinnerIcon spinnerBackgroundColor="#00000000" />
                </div>
                <span>Processing...</span>
              </span>
              <img src={env?.videoProcessingGIF} alt="" />
            </VideoPreview>
          </RenderIf>
        </VideoSection>
        <RenderIf condition={!!description}>
          <DescriptionSection>
            <Description color={style.heading2}>{description}</Description>
          </DescriptionSection>
        </RenderIf>
        <RenderIf condition={showCta}>
          <ActionSection>
            <CallToAction
              styles={style}
              message={callToAction}
              trackCTAClick={trackCTAClick}
              isMultipleCTAsSupported={isMultipleCTAsSupported}
            />
          </ActionSection>
        </RenderIf>
      </MainContent>
      <RenderIf condition={!!campaign?.sendsparkBrandingEnabled}>
        <Footer>
          <VideoMessageSign>
            Video Message by
            <LogoLink
              onClick={trackViralLink}
              className="logo-link"
              href={viralLink}
              target="_blank"
              rel="noreferrer"
            >
              <LogoFooter />
            </LogoLink>
          </VideoMessageSign>
        </Footer>
      </RenderIf>
    </LegacyAndV2>
  )
}

export default LegacyTheme